/**
 * The calm "saved, but Backoffice isn't updated yet" notice.
 *
 * A Hub edit is saved before it is pushed to Backoffice, so a Backoffice
 * failure never fails the edit: the person is told it saved and that
 * Backoffice is behind. The reason is for the team and lives on the persisted
 * marker, not in the toast.
 */

import { notifications } from '@mantine/notifications';

import { isBackofficeBehind, type BackofficeSync } from '@/types/backoffice-sync';

export const BACKOFFICE_BEHIND_TITLE = 'Saved — Backoffice not updated yet';

/** Shows the notice if the push this edit triggered did not land. Returns
 * whether it did, so a caller can skip its usual success toast. */
export function notifyIfBackofficeBehind(sync: BackofficeSync | null | undefined): boolean {
  if (!isBackofficeBehind(sync)) {
    return false;
  }
  notifications.show({
    title: BACKOFFICE_BEHIND_TITLE,
    message:
      'Your change is saved in GTI Hub, but Backoffice could not be updated. It stays flagged on this page until Backoffice catches up.',
    color: 'orange',
  });
  return true;
}

/** The same notice for an edit that fans out to several Backoffice projects
 * (a case rename or a change of owner). Returns whether it was shown. */
export function notifyBackofficeProjectsBehind(failed: number): boolean {
  if (failed <= 0) {
    return false;
  }
  notifications.show({
    title: BACKOFFICE_BEHIND_TITLE,
    message: `Your change is saved in GTI Hub, but ${failed} Backoffice project${
      failed === 1 ? '' : 's'
    } could not be updated. ${failed === 1 ? 'It is' : 'They are'} flagged on the report${
      failed === 1 ? '' : 's'
    } until Backoffice catches up.`,
    color: 'orange',
  });
  return true;
}
